import Axios, { AxiosInstance } from "axios";
import { startLoading, stopLoading } from "../composables/useFocusTrap";
import { delegationContext } from "./delegationContext";
import { useAuth } from "../store/auth";

// We create our own axios instance and set a custom base URL.
// Note that if we wouldn't set any config here we do not need
// a named export, as we could just `import axios from 'axios'`
const axios: AxiosInstance & {
  CancelToken: typeof Axios.CancelToken;
  isCancel: typeof Axios.isCancel;
} = Axios.create({
  withCredentials: true,
}) as any;

axios.CancelToken = Axios.CancelToken;
axios.isCancel = Axios.isCancel;

export default axios;

export const CancelToken = Axios.CancelToken;

// etag cache based on https://github.com/raulanatol/axios-etag-cache/
const cache = new Map();

export function invalidateCache(url?: string) {
  if (url) {
    cache.delete(url);
  } else {
    cache.clear();
  }
}
function isCacheableMethod(config: any) {
  return ~["GET", "HEAD"].indexOf(config.method.toUpperCase());
}
const getHeaderCaseInsensitive = (headerName: string, headers: Record<string, any> = {}) => {
  const key = Object.keys(headers).find(
    (x) => x.toLocaleLowerCase() === headerName.toLocaleLowerCase(),
  );
  return key ? headers[key] : undefined;
};

const mapCancelTokens = new Map<string, () => void>();

axios.interceptors.request.use(async (config) => {
  startLoading();

  // Delegated working agreements: an explicit per-call tenant option, targeting this
  // call only — no ambient/global state consulted. Booking's chosen mechanism (see
  // plan-iteration-2.md Cycle 20) since its call sites are a small, enumerable set.
  // Takes precedence over the ambient delegation context below regardless — a banner
  // saying one tenant while an explicit call targets another must never both apply.
  // Superseded by the transactional-api option below for the DWA booking flow (see
  // plan-iteration-9.md) -- left in place since nothing else has migrated off it yet.
  let explicitDelegateApplied = false;
  if (config.tenantId) {
    const auth = useAuth();
    const delegateTenant = auth.tenants?.[config.tenantId];
    if (delegateTenant) {
      config.baseURL = delegateTenant.url;
      config.headers.set("X-Delegated-Tenant-ID", auth.selectedTenant);
      explicitDelegateApplied = true;
    }
  }

  // DWA transactional-api calls (plan-iteration-9.md): mints a fresh, short-lived token
  // immediately before this one call and attaches it as a bearer token -- never held or
  // reused across calls, matching TransactionalAPIAuthenticator's ~120s expiry enforcement.
  // No X-Delegated-Tenant-ID header at all here; the JWT itself carries the caller's identity
  // and permissions, replacing that ambient-header trust model entirely for this flow.
  //
  // The Hub tenant is deliberately NOT looked up in auth.tenants (unlike the tenantId/ambient
  // branches above/below) -- that map is populated from IAM's own /user/info and only ever
  // contains tenants the logged-in user has personal multi-practice access to. A DWA
  // relationship grants no such access -- it's a tenant-to-tenant trust relationship, not an
  // IAM-level user grant -- so auth.tenants[hubTenantId] is reliably undefined for a genuine
  // delegate Hub, and this call would silently fall through to the default (home) baseURL
  // instead of erroring. Derive the Hub's URL the same way the backend's own
  // ThirdPartyApplicationService::buildTenantJwksUrl() does: take the current (home) tenant's
  // own URL -- always present in auth.tenants, since the user is by definition logged into it
  // -- and swap its leading tenant-id subdomain label for the Hub's.
  if (config.transactionalApiTenantId) {
    const auth = useAuth();
    // selectedTenant/tenants are both non-null here by the invariant described above:
    // the user is logged into their home tenant, so it is always in the map.
    const homeTenant = auth.tenants![auth.selectedTenant!];
    const delegateBaseUrl = homeTenant.url.replace(
      /^(https?:\/\/)[^.]+\./,
      `$1${config.transactionalApiTenantId}.`,
    );
    const { data } = await axios.post(
      "/integrations/delegated-working-agreement/generate-transactional-api-token",
      { delegateTenantId: config.transactionalApiTenantId },
    );
    config.baseURL = delegateBaseUrl + "/transactional-api";
    config.headers.set("Authorization", `Bearer ${data.token}`);
    explicitDelegateApplied = true;
  }

  // Ambient delegation context (generalised from the crossTenantPatient spike — see
  // plan-iteration-2.md Cycle 29). When the current workflow is scoped to another workspace,
  // send its requests and saves there instead of the current one. It never sends page/UI files
  // there — those always come from the current workspace, since the app itself is the same
  // everywhere. Only consulted when no explicit per-call option already applied (see above).
  // Previously also gated on a URL-prefix allowlist ("patient/"/"clinical/" only), on the theory
  // that this would stop ambient context leaking into unrelated global UI (menus, other pages).
  // Dropped — it was never actually scoped to "record" work, just to whichever domain prefixes
  // happened to be enumerated, and every domain missed from the list broke silently instead of
  // falling back to the home tenant (confirmed for referrals/data/create-referral-method-selection
  // and staff/data/list-absences). The context's own lifecycle (global.ts) already scopes when
  // it's active, based on the current route — so the prefix check here was redundant when
  // complete and actively harmful when it inevitably fell behind new domains.
  if (!explicitDelegateApplied && delegationContext.targetTenantUrl) {
    const path = (config.url || "").replace(/^\//, "");
    const isUiTemplate = path.includes("/ui/") || path.startsWith("ui/") || path.endsWith(".vue");
    if (!isUiTemplate) {
      config.baseURL = delegationContext.targetTenantUrl;
      config.headers.set("X-Delegated-Tenant-ID", delegationContext.homeTenantId);
      config.skipEtag = true;
    }
  }

  // The app caches responses by web address only, so for a cross-workspace request we skip the
  // cache (skipEtag) — otherwise one workspace's cached data could be shown for another.
  if (isCacheableMethod(config) && !config.skipEtag) {
    const uuid = config.url;
    const lastCachedResult = cache.get(uuid);
    if (lastCachedResult) {
      // @ts-expect-error types not fully match
      config.headers = { ...config.headers, "If-None-Match": lastCachedResult.etag };
    }
  }

  if (!config.cancelToken && config.cancelRequestId) {
    mapCancelTokens.get(config.cancelRequestId)?.();

    config.cancelToken = new Axios.CancelToken((cancel) => {
      mapCancelTokens.set(config.cancelRequestId!, cancel);
    });
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    stopLoading();
    if (isCacheableMethod(response.config) && !response.config.skipEtag) {
      const responseETAG = getHeaderCaseInsensitive("etag", response.headers);
      if (responseETAG) {
        cache.set(response.config.url, { etag: responseETAG, data: response.data });
      }
    }
    return response;
  },
  (error) => {
    stopLoading();

    //Don't do anything if is a cancel or a failed panic button pairing attempt
    if (axios.isCancel(error)) {
      return new Promise(() => {});
    }

    if (error.response && error.response.status === 304) {
      const getCachedResult = cache.get(error.response.config.url);
      if (!getCachedResult) {
        return Promise.reject(error);
      }
      const newResponse = error.response;
      newResponse.status = 200;
      newResponse.data = getCachedResult.data;
      return Promise.resolve(newResponse);
    }

    return Promise.reject(error);
  },
);

// / etag cache
